/**
 * @file index.tsx
 *
 * @fileoverview Renders a form with an email field for the user to submit.
 * This will call a mutation to send a verification email
 * to the user for that email address.
 */
import React from 'react';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Yup from 'yup';
import PASSWORD_RESET from '~/jobiqo/components/User/PasswordReset/graphql/mutations/passwordReset.gql';
import { useMutation } from '@apollo/client';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import { Branding } from '~/jobiqo/components/Branding';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import { CenterBox } from '~/vcl/atoms/06-layout/01-alignment/02-center-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { EmailSentIcon } from '~/vcl/icons/EmailSent';
import { Box } from '~/vcl/atoms/07-box';
import { CenterForm } from '~/vcl/organisms/01-form/01-center';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { LoadingDotsIcon } from '~/vcl/icons/LoadingDots';
import { EnvelopeIcon } from '~/vcl/icons/Envelope';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import RichFormattedMessage from '~/jobiqo/components/RichFormattedMessage';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { HeaderLoginPagesSlot } from '~/jobiqo/components/Slots/HeaderLoginPagesSlot';

interface FormValues {
  email: string;
}

export default function PasswordReset() {
  const intl = useIntl();
  const frontendConfig = useFrontendConfig();
  const READ_ONLY_MODE = frontendConfig.APP.READ_ONLY_MODE;
  const frontendTheme = getFrontendTheme(frontendConfig);
  const { EMAIL_VALIDATION } = getCommonTranslations();

  const FormSchema = Yup.object().shape({
    email: Yup.string()
      .email(EMAIL_VALIDATION)
      .required(
        intl.formatMessage({
          id: 'email_required',
          defaultMessage: 'Email is required',
        }),
      ),
  });
  const { handleSubmit, control } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(FormSchema),
    defaultValues: {
      email: '',
    },
  });
  let errorList: React.ReactNode = null;
  const [passwordReset, { data, loading, error }] = useMutation(PASSWORD_RESET);

  if (error) {
    errorList = (
      <MessageAlert alertStyle="danger">
        <FormattedMessage
          id="reset_password_error"
          defaultMessage="There was an error resetting your password"
        />
      </MessageAlert>
    );
  }

  if (data && data.passwordReset && data.passwordReset.errors.length > 0) {
    errorList = <ContentErrorList errors={data.passwordReset.errors} />;
  }

  const isSubmitted = !errorList && data && data.passwordReset;

  return (
    <Main>
      <CenterBox>
        {isSubmitted ? (
          <AlignedCenter>
            <Spacing size="large" />
            <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
            <Spacing size="medium" />
            <EmailSentIcon
              color="primary"
              title="Submited"
              width="200px"
              height="200px"
            />
            <Spacing />
            <Text textAlign="center" data-test-id="reset-password-submitted">
              <FormattedMessage
                id="reset_password_submitted"
                defaultMessage="A link has been sent to your email to reset your password. {br}
            Check your email and click the link inside it."
                values={{
                  br: <br />,
                }}
              />
            </Text>
          </AlignedCenter>
        ) : (
          <>
            <HeaderLoginPagesSlot />
            <Spacing size="large" />
            <Branding maxHeight={frontendTheme.logo.singlePageMaxHeight} />
            <Spacing size="medium" />
            <Text tag="h1" data-test-id="reset-password-title">
              <FormattedMessage
                id="reset_password_title"
                defaultMessage="Reset your password"
              />
            </Text>
            <Text textAlign="center">
              <FormattedMessage
                id="reset_password_info"
                defaultMessage="Provide your email address and an email will be
          sent {br} so that you can reset your password."
                values={{
                  br: <br />,
                }}
              />
            </Text>
            <Box>
              {errorList && (
                <>
                  {errorList}
                  <div data-test-id="reset-password-error"></div>
                  <Spacing />
                </>
              )}
              {READ_ONLY_MODE && (
                <div
                  style={{
                    maxWidth: '400px',
                  }}
                >
                  <Spacing marginBottom={6}>
                    <MessageAlert alertStyle="danger">
                      <RichFormattedMessage
                        id="reset_pass_block_readonly"
                        defaultMessage="Resetting of password is blocked temporarily while the site is in maintenance mode."
                      />
                    </MessageAlert>
                  </Spacing>
                </div>
              )}
              <CenterForm
                minWidth="250px"
                onSubmit={handleSubmit((formData: FormValues) => {
                  if (!READ_ONLY_MODE) {
                    passwordReset({
                      variables: formData,
                    });
                  }
                })}
              >
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        error={!!error}
                        type="email"
                        size={40}
                        label={intl.formatMessage({
                          id: 'email',
                          defaultMessage: 'Email',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'email_placeholder',
                          defaultMessage: 'Enter an email address',
                        })}
                        data-test-id="email_password_reset"
                      />
                      {error && <InlineError message={error.message} />}
                    </>
                  )}
                />
                <Spacing />
                <Button
                  data-test-id="reset-password-submit"
                  disabled={loading || READ_ONLY_MODE}
                  type="submit"
                  buttonStyle="primary"
                >
                  {loading ? (
                    <LoadingDotsIcon title="Loading" />
                  ) : (
                    <>
                      <EnvelopeIcon title="reset" aria-hidden="true" />
                      <span>
                        <FormattedMessage
                          id="reset_password"
                          defaultMessage="Reset password"
                        />
                      </span>
                    </>
                  )}
                </Button>
              </CenterForm>
            </Box>
          </>
        )}
      </CenterBox>
    </Main>
  );
}
export { PasswordReset };
